import { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  MenuItem,
  FormControl,
  Select,
  Button,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  TextField,
  Checkbox,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRowModel,
  type GridRenderEditCellParams,
  type GridCellParams,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { selectAssets } from 'store/asset.slice';
import useAppTheme from 'hooks/useAppTheme';
import { useRoleBasedAccess } from 'hooks/useRoleBasedAccess';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import ReportAPI from 'api/report.api';
import AddTripsRowModal, { type NewTripRowPayload } from './AddTripsRowModal';

dayjs.extend(utc);
dayjs.extend(timezone);

const EDITABLE_ROLES = [
  'director',
  'internal-user',
  'external-superadmin',
  'external-admin',
];

const downloadCsv = (rows: any[], filename: string) => {
  const headers = [
    'Truck',
    'Time Full',
    'Time Empty',
    'Assigned Shovel',
    'Shovel',
    'Material',
    'Blast',
    'Blast Type',
    'Blast Region',
    'Grades',
    'Assigned Dump',
    'Dump',
    'Computed Tonnage',
    'Computed Volume',
    'Notes',
  ];
  const csvRows = [
    headers.join(','),
    ...rows.map((r) =>
      [
        r.truck ?? '',
        r.timeFull ? dayjs(r.timeFull).format('DD/MM/YY HH:mm') : '',
        r.timeEmpty ? dayjs(r.timeEmpty).format('DD/MM/YY HH:mm') : '',
        r.assignedShovel ?? '',
        r.shovel ?? '',
        r.material ?? '',
        r.blast ?? '',
        r.blastType ?? '',
        r.blastRegion ?? '',
        r.grades ?? '',
        r.assignedDump ?? '',
        r.dump ?? '',
        r.computedTonnage != null ? Number(r.computedTonnage).toFixed(2) : '',
        r.computedVolume != null ? Number(r.computedVolume).toFixed(2) : '',
        r.notes ?? '',
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    ),
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const DropdownEditCell = ({
  id,
  field,
  value,
  options,
}: GridRenderEditCellParams & { options: string[] }) => {
  const apiRef = useGridApiContext();
  // Open the menu automatically as soon as edit mode begins, so a single click
  // on the cell opens the dropdown instead of requiring a second click.
  const [open, setOpen] = useState(true);
  return (
    <Select
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
        apiRef.current.stopCellEditMode({ id, field });
      }}
      value={value ?? ''}
      onChange={(e) => {
        void apiRef.current.setEditCellValue({
          id,
          field,
          value: e.target.value,
        });
        apiRef.current.stopCellEditMode({ id, field });
      }}
      size="small"
      displayEmpty
      fullWidth
      sx={{ fontSize: '0.875rem' }}
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>
      {options.map((opt) => (
        <MenuItem key={opt} value={opt}>
          {opt}
        </MenuItem>
      ))}
    </Select>
  );
};

const TripsReport = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const gridApiRef = useGridApiRef();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const shiftPeriods = useAppSelector(
    (state) => state.customerShiftConfigReducer.data?.shiftPeriods ?? []
  );

  const canEdit = useRoleBasedAccess(EDITABLE_ROLES);

  const today = dayjs().format('YYYY-MM-DD');
  const [mode, setMode] = useState<'single' | 'range'>('single');

  const [date, setDate] = useState<string>(today);
  // One or more shifts may be selected; at least one is required to fetch.
  const [shifts, setShifts] = useState<string[]>([]);

  const [rangeStart, setRangeStart] = useState<string>('');
  const [rangeEnd, setRangeEnd] = useState<string>('');

  const [rows, setRows] = useState<any[]>([]);

  // Client-side filters — applied to already-fetched rows only
  const [truckFilter, setTruckFilter] = useState<string | null>(null);
  const [shovelFilter, setShovelFilter] = useState<string | null>(null);
  const [dumpFilter, setDumpFilter] = useState<string | null>(null);
  const [blastFilter, setBlastFilter] = useState<string | null>(null);

  const assets = useAppSelector(selectAssets);

  const uniqueShovels = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.shovel).filter(Boolean))).sort(
        (a, b) => (a as string).localeCompare(b as string)
      ) as string[],
    [rows]
  );

  const uniqueDumps = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.dump).filter(Boolean))).sort(
        (a, b) => (a as string).localeCompare(b as string)
      ) as string[],
    [rows]
  );

  // Truck options — all assets for the logged-in customer (bumper numbers),
  // from the Redux asset slice.
  const truckOptions = useMemo(() => {
    const set = new Set<string>();
    assets.forEach((a) => {
      if (a.bumperNumber) set.add(a.bumperNumber);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [assets]);

  // Distinct blasts present in the fetched data
  const uniqueBlasts = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.blast).filter(Boolean))).sort(
        (a, b) => (a as string).localeCompare(b as string)
      ) as string[],
    [rows]
  );

  // Rows shown in the grid / exported to CSV after applying all filters.
  // The filters compose independently — order of selection doesn't matter,
  // and clearing one keeps the others applied.
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const truckOk =
        !truckFilter || r.truck?.toLowerCase() === truckFilter.toLowerCase();
      const shovelOk =
        !shovelFilter || r.shovel?.toLowerCase() === shovelFilter.toLowerCase();
      const dumpOk =
        !dumpFilter || r.dump?.toLowerCase() === dumpFilter.toLowerCase();
      const blastOk =
        !blastFilter || r.blast?.toLowerCase() === blastFilter.toLowerCase();
      return truckOk && shovelOk && dumpOk && blastOk;
    });
  }, [rows, truckFilter, shovelFilter, dumpFilter, blastFilter]);

  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [lastGeneratedDate, setLastGeneratedDate] = useState<string | null>(
    null
  );

  const shiftOptions =
    shiftPeriods.length > 0
      ? shiftPeriods
      : [{ shiftName: 'Day Shift' }, { shiftName: 'Night Shift' }];

  const inputOutlineSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: palette.borderGrey },
      '&:hover fieldset': { borderColor: palette.borderGrey },
      '&.Mui-focused fieldset': {
        borderColor: palette.borderGrey,
        borderWidth: 1.5,
      },
      '& .MuiInputBase-input': { color: 'text.primary' },
      '& .MuiSvgIcon-root': { color: 'text.primary' },
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      '&.Mui-focused': { color: 'text.primary' },
    },
    '& .MuiInputBase-root': { height: 40 },
  } as const;

  const isRangeDayDisabled = (day: Dayjs): boolean => {
    const dayStr = day.format('YYYY-MM-DD');
    const monthAgo = dayjs().subtract(1, 'month').startOf('day');
    if (day.isBefore(monthAgo)) return true;
    if (day.isAfter(dayjs())) return true;
    if (dayStr === today && lastGeneratedDate !== today) return true;
    return false;
  };

  const handleFetch = async () => {
    if (!customerId) return;
    if (mode === 'single' && (!date || shifts.length === 0)) return;
    if (mode === 'range' && (!rangeStart || !rangeEnd || shifts.length === 0))
      return;

    setLoading(true);
    try {
      const api = new ReportAPI();
      let data: any[];

      if (mode === 'single') {
        data = (await api.getTripsReport({
          customerId,
          date,
          shift: shifts,
        })) as any[];
      } else {
        data = (await api.getTripsReport({
          customerId,
          dateFrom: rangeStart,
          dateTo: rangeEnd,
          shift: shifts,
        })) as any[];
      }

      setRows(data.map((r: any, i: number) => ({ ...r, _gridId: r.id ?? i })));
      setTruckFilter(null);
      setShovelFilter(null);
      setDumpFilter(null);
      setBlastFilter(null);

      if (data.length > 0) {
        const latest = data.reduce((max: string, r: any) => {
          const d = r.date ?? '';
          return d > max ? d : max;
        }, '');
        if (latest) setLastGeneratedDate(latest);
      }

      setFetched(true);
    } catch (err) {
      console.error('Failed to fetch trips report', err);
    } finally {
      setLoading(false);
    }
  };

  const processRowUpdate = async (newRow: GridRowModel) => {
    if (!newRow.id) return newRow;
    const api = new ReportAPI();
    await api.updateTripsReportRow({
      id: newRow.id,
      customerId,
      assignedShovel: newRow.assignedShovel,
      assignedDump: newRow.assignedDump,
      notes: newRow.notes,
    });
    setRows((prev) =>
      prev.map((r) => (r.id === newRow.id ? { ...r, ...newRow } : r))
    );
    return newRow;
  };

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const handleAddRow = async (payload: NewTripRowPayload) => {
    try {
      const api = new ReportAPI();
      await api.createTripsReportRow({ customerId, ...payload });
      dispatch(
        showNotification({
          message: t('schedule_report_row_added'),
          type: 'success',
        })
      );
      await handleFetch();
    } catch (err) {
      console.error('Failed to add trips report row', err);
      dispatch(
        showNotification({
          message: t('schedule_report_row_add_failed'),
          type: 'error',
        })
      );
      throw err;
    }
  };

  const handleDeleteRow = async (row: any) => {
    if (!row.id) return;
    setDeletingId(row.id);
    try {
      const api = new ReportAPI();
      await api.deleteTripsReportRow({ id: row.id, customerId });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      dispatch(
        showNotification({
          message: t('schedule_report_row_deleted'),
          type: 'success',
        })
      );
    } catch (err) {
      console.error('Failed to delete trips report row', err);
      dispatch(
        showNotification({
          message: t('schedule_report_row_delete_failed'),
          type: 'error',
        })
      );
    } finally {
      setDeletingId(null);
    }
  };

  const columns: GridColDef[] = [
    { field: 'truck', headerName: 'Truck', width: 100 },
    {
      field: 'timeFull',
      headerName: 'Time Full',
      width: 140,
      valueFormatter: ({ value }: { value: unknown }) =>
        value ? dayjs(value as string).format('DD/MM/YY HH:mm') : '',
    },
    {
      field: 'timeEmpty',
      headerName: 'Time Empty',
      width: 140,
      valueFormatter: ({ value }: { value: unknown }) =>
        value ? dayjs(value as string).format('DD/MM/YY HH:mm') : '',
    },
    {
      field: 'assignedShovel',
      headerName: 'Assigned Shovel',
      width: 155,
      editable: canEdit,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 0.5,
          }}
        >
          {canEdit && (
            <EditIcon sx={{ fontSize: 13, opacity: 0.4, flexShrink: 0 }} />
          )}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </span>
        </Box>
      ),
      renderEditCell: (params) => (
        <DropdownEditCell {...params} options={uniqueShovels} />
      ),
    },
    { field: 'shovel', headerName: 'Shovel', width: 130 },
    { field: 'material', headerName: 'Material', width: 120 },
    { field: 'blast', headerName: 'Blast', width: 130 },
    { field: 'blastType', headerName: 'Blast Type', width: 110 },
    { field: 'blastRegion', headerName: 'Blast Region', width: 110 },
    { field: 'grades', headerName: 'Grades', width: 110 },
    {
      field: 'assignedDump',
      headerName: 'Assigned Dump',
      width: 145,
      editable: canEdit,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 0.5,
          }}
        >
          {canEdit && (
            <EditIcon sx={{ fontSize: 13, opacity: 0.4, flexShrink: 0 }} />
          )}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </span>
        </Box>
      ),
      renderEditCell: (params) => (
        <DropdownEditCell {...params} options={uniqueDumps} />
      ),
    },
    { field: 'dump', headerName: 'Dump', width: 110 },
    {
      field: 'computedTonnage',
      headerName: 'Tonnage',
      width: 100,
      type: 'number',
      valueFormatter: ({ value }: { value: unknown }) =>
        value != null ? Number(value).toFixed(2) : '',
    },
    {
      field: 'computedVolume',
      headerName: 'Volume',
      width: 100,
      type: 'number',
      valueFormatter: ({ value }: { value: unknown }) =>
        value != null ? Number(value).toFixed(2) : '',
    },
    {
      field: 'notes',
      headerName: 'Notes',
      width: 220,
      editable: canEdit,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 0.5,
          }}
        >
          {canEdit && (
            <EditIcon sx={{ fontSize: 13, opacity: 0.4, flexShrink: 0 }} />
          )}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </span>
        </Box>
      ),
    },
    {
      field: '_delete',
      headerName: 'Action',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      renderCell: ({ row }) =>
        canEdit ? (
          <IconButton
            size="small"
            color="error"
            disabled={deletingId === row.id}
            onClick={() => {
              void handleDeleteRow(row);
            }}
          >
            {deletingId === row.id ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        ) : null,
    },
  ];

  const canFetch =
    mode === 'single'
      ? Boolean(date && shifts.length > 0 && !loading)
      : Boolean(rangeStart && rangeEnd && shifts.length > 0 && !loading);

  const csvFilename =
    mode === 'single'
      ? `trips-report-${date}-${shifts
          .map((s) => s.replace(/\s*shift\s*/i, '').trim())
          .join('-')} Shift.csv`
      : `trips-report-${rangeStart}-to-${rangeEnd}.csv`;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        bgcolor: 'background.paper',
        width: { xs: '100%', md: '80%' },
        flexBasis: { md: '80%' },
        flexGrow: 1,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, color: 'text.primary', mb: 2.5 }}
      >
        {t('trips_report_title')}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, val) => {
            if (!val) return;
            setMode(val);
            setFetched(false);
            setRows([]);
            setTruckFilter(null);
            setShovelFilter(null);
            setDumpFilter(null);
            setBlastFilter(null);
          }}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 2,
              fontSize: '0.8rem',
              color: 'text.secondary',
              borderColor: palette.borderGrey,
              '&.Mui-selected': {
                color: 'text.primary',
                bgcolor: enableDarkTheme
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(0,0,0,0.06)',
              },
            },
          }}
        >
          <ToggleButton value="single">{t('single_date')}</ToggleButton>
          <ToggleButton value="range">{t('date_range')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Stack
        direction="row"
        alignItems="flex-end"
        sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}
      >
        {mode === 'single' ? (
          <>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                {t('date')}
              </Typography>
              <DatePicker
                value={date ? dayjs(date) : null}
                maxDate={dayjs()}
                onChange={(v) => {
                  setDate(v?.format('YYYY-MM-DD') ?? '');
                  setFetched(false);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: { readOnly: true },
                    sx: { ...inputOutlineSx, width: 180 },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                {t('shift')}
              </Typography>
              <FormControl size="small" sx={{ ...inputOutlineSx, width: 200 }}>
                <Select
                  multiple
                  value={shifts}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value;
                    setShifts(typeof val === 'string' ? val.split(',') : val);
                    setFetched(false);
                  }}
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      <span style={{ color: '#aaa' }}>
                        {t('select_shifts')}
                      </span>
                    ) : (
                      selected.join(', ')
                    )
                  }
                >
                  {shiftOptions.map((s) => (
                    <MenuItem key={s.shiftName} value={s.shiftName}>
                      <Checkbox checked={shifts.includes(s.shiftName)} />
                      <ListItemText primary={s.shiftName} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        ) : (
          <>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                {t('from')}
              </Typography>
              <DatePicker
                value={rangeStart ? dayjs(rangeStart) : null}
                maxDate={rangeEnd ? dayjs(rangeEnd) : dayjs()}
                minDate={dayjs().subtract(1, 'month').startOf('day')}
                shouldDisableDate={isRangeDayDisabled}
                onChange={(v) => {
                  setRangeStart(v?.format('YYYY-MM-DD') ?? '');
                  setFetched(false);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: { readOnly: true },
                    sx: { ...inputOutlineSx, width: 160 },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                {t('to')}
              </Typography>
              <DatePicker
                value={rangeEnd ? dayjs(rangeEnd) : null}
                minDate={
                  rangeStart
                    ? dayjs(rangeStart)
                    : dayjs().subtract(1, 'month').startOf('day')
                }
                maxDate={
                  rangeStart
                    ? dayjs(rangeStart).add(1, 'month').isAfter(dayjs())
                      ? dayjs()
                      : dayjs(rangeStart).add(1, 'month')
                    : dayjs()
                }
                shouldDisableDate={isRangeDayDisabled}
                onChange={(v) => {
                  setRangeEnd(v?.format('YYYY-MM-DD') ?? '');
                  setFetched(false);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: { readOnly: true },
                    sx: { ...inputOutlineSx, width: 160 },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                {t('shift')}
              </Typography>
              <FormControl size="small" sx={{ ...inputOutlineSx, width: 200 }}>
                <Select
                  multiple
                  value={shifts}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value;
                    setShifts(typeof val === 'string' ? val.split(',') : val);
                    setFetched(false);
                  }}
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      <span style={{ color: '#aaa' }}>
                        {t('select_shifts')}
                      </span>
                    ) : (
                      selected.join(', ')
                    )
                  }
                >
                  {shiftOptions.map((s) => (
                    <MenuItem key={s.shiftName} value={s.shiftName}>
                      <Checkbox checked={shifts.includes(s.shiftName)} />
                      <ListItemText primary={s.shiftName} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        <Button
          variant="contained"
          onClick={handleFetch}
          disabled={!canFetch}
          sx={{ textTransform: 'none', height: 40 }}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            t('fetch')
          )}
        </Button>

        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
          >
            {t('truck')}
          </Typography>
          <Autocomplete
            size="small"
            disabled={!fetched}
            options={truckOptions}
            value={truckFilter}
            onChange={(_, val) => {
              setTruckFilter(val);
            }}
            sx={{ width: 190 }}
            slotProps={{
              paper: {
                sx: { width: 'max-content', minWidth: 190, maxWidth: 320 },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('all_trucks')}
                sx={inputOutlineSx}
              />
            )}
          />
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
          >
            {t('trips_report_shovel')}
          </Typography>
          <Autocomplete
            size="small"
            disabled={!fetched || uniqueShovels.length === 0}
            options={uniqueShovels}
            value={shovelFilter}
            onChange={(_, val) => {
              setShovelFilter(val);
            }}
            sx={{ width: 190 }}
            slotProps={{
              paper: {
                sx: { width: 'max-content', minWidth: 190, maxWidth: 320 },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('trips_report_all_shovels')}
                sx={inputOutlineSx}
              />
            )}
          />
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
          >
            {t('dump')}
          </Typography>
          <Autocomplete
            size="small"
            disabled={!fetched || uniqueDumps.length === 0}
            options={uniqueDumps}
            value={dumpFilter}
            onChange={(_, val) => {
              setDumpFilter(val);
            }}
            sx={{ width: 190 }}
            slotProps={{
              paper: {
                sx: { width: 'max-content', minWidth: 190, maxWidth: 320 },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('all_dumps')}
                sx={inputOutlineSx}
              />
            )}
          />
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
          >
            {t('trips_report_blast')}
          </Typography>
          <Autocomplete
            size="small"
            disabled={!fetched || uniqueBlasts.length === 0}
            options={uniqueBlasts}
            value={blastFilter}
            onChange={(_, val) => {
              setBlastFilter(val);
            }}
            sx={{ width: 190 }}
            slotProps={{
              paper: {
                sx: { width: 'max-content', minWidth: 190, maxWidth: 320 },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('trips_report_all_blasts')}
                sx={inputOutlineSx}
              />
            )}
          />
        </Box>
      </Stack>

      {fetched && (
        <>
          {canEdit && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setAddOpen(true);
                }}
                sx={{
                  textTransform: 'none',
                  borderColor: palette.borderGrey,
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: palette.borderGrey,
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                Add Row
              </Button>
            </Stack>
          )}

          <Box sx={{ height: 480, width: '100%' }}>
            {filteredRows.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 2 }}
              >
                No data found for the selected period.
              </Typography>
            ) : (
              <DataGrid
                apiRef={gridApiRef}
                rows={filteredRows}
                columns={columns}
                getRowId={(r) => r._gridId ?? r.id}
                onCellClick={(params: GridCellParams) => {
                  // Single click enters edit mode for editable cells, so the
                  // dropdowns open on the first click instead of a double-click.
                  if (
                    params.isEditable &&
                    gridApiRef.current.getCellMode(params.id, params.field) ===
                      'view'
                  ) {
                    gridApiRef.current.startCellEditMode({
                      id: params.id,
                      field: params.field,
                    });
                  }
                }}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={(err) => {
                  console.error('Row update failed:', err);
                }}
                pageSizeOptions={[25, 50, 100]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                sx={{
                  border: `1px solid ${palette.borderGrey}`,
                  '& .MuiDataGrid-columnHeaders': {
                    bgcolor: 'background.default',
                  },
                }}
              />
            )}
          </Box>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={filteredRows.length === 0}
              onClick={() => {
                downloadCsv(filteredRows, csvFilename);
              }}
              sx={{
                textTransform: 'none',
                borderColor: palette.borderGrey,
                color: 'text.primary',
                '&:hover': {
                  borderColor: palette.borderGrey,
                  backgroundColor: 'action.hover',
                },
              }}
            >
              Download CSV
            </Button>
          </Stack>
        </>
      )}

      <AddTripsRowModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        onSubmit={handleAddRow}
        shiftOptions={shiftOptions.map((s) => s.shiftName)}
        truckOptions={truckOptions}
        shovelOptions={uniqueShovels}
        dumpOptions={uniqueDumps}
        inputOutlineSx={inputOutlineSx}
      />
    </Paper>
  );
};

export default TripsReport;
